<template>
  <div class="dmg-panel">
    <!-- ── Header bar with stats + filters ──────────────────── -->
    <div class="dmg-toolbar">
      <div class="dmg-stats">
        <div class="dmg-stat">
          <div class="dmg-stat__icon dmg-stat__icon--total">
            <feather-icon icon="AlertTriangleIcon" size="16"/>
          </div>
          <div>
            <div class="dmg-stat__value">{{ pagination.total || 0 }}</div>
            <div class="dmg-stat__label">{{ $t('Total damages') || 'Total' }}</div>
          </div>
        </div>
        <div class="dmg-stat">
          <div class="dmg-stat__icon dmg-stat__icon--open">
            <feather-icon icon="CircleIcon" size="16"/>
          </div>
          <div>
            <div class="dmg-stat__value">{{ openCount }}</div>
            <div class="dmg-stat__label">{{ $t('Open') || 'Open' }}</div>
          </div>
        </div>
        <div class="dmg-stat">
          <div class="dmg-stat__icon dmg-stat__icon--fixed">
            <feather-icon icon="CheckCircleIcon" size="16"/>
          </div>
          <div>
            <div class="dmg-stat__value">{{ fixedCount }}</div>
            <div class="dmg-stat__label">{{ $t('Fixed') || 'Fixed' }}</div>
          </div>
        </div>
      </div>

      <div class="dmg-filters">
        <button
            class="dmg-filter"
            :class="{ active: filter.fixed }"
            @click="toggleFixed"
        >
          <feather-icon :icon="filter.fixed ? 'EyeIcon' : 'EyeOffIcon'" size="13" class="mr-25"/>
          {{ $t('showFixed') }}
        </button>
        <button
            class="dmg-filter"
            :class="{ active: filter.paid }"
            @click="togglePaid"
        >
          <feather-icon :icon="filter.paid ? 'EyeIcon' : 'EyeOffIcon'" size="13" class="mr-25"/>
          {{ $t('showPayed') }}
        </button>
      </div>
    </div>

    <!-- ── Loading ──────────────────────────────────────────── -->
    <div v-if="loading" class="dmg-loading">
      <b-spinner variant="primary" small class="mr-50"/>
      <span>{{ msg('Loading') }}…</span>
    </div>

    <!-- ── Cards grid ───────────────────────────────────────── -->
    <div v-else-if="damages.length" class="dmg-grid">
      <article
          v-for="d in damages"
          :key="d.id"
          class="dmg-card"
          :class="{ 'dmg-card--fixed': d.damageFixed, 'dmg-card--paid': d.paid }"
          @click="onItemSelected(d)"
      >
        <!-- accent stripe -->
        <span class="dmg-card__stripe" :class="d.damageFixed ? 'dmg-card__stripe--fixed' : 'dmg-card__stripe--open'"></span>

        <!-- top row: client + status -->
        <header class="dmg-card__head">
          <div class="dmg-card__client">
            <div class="dmg-card__avatar">{{ initials(d.clientName) }}</div>
            <div class="dmg-card__client-info">
              <div class="dmg-card__name">{{ d.clientName || msg('Unknown client') }}</div>
              <div class="dmg-card__time">
                <feather-icon icon="ClockIcon" size="11" class="mr-25"/>
                {{ d.reportedTime | moment('DD/MM/YY HH:mm') }}
              </div>
            </div>
          </div>
          <div class="dmg-card__badges">
            <b-badge v-if="d.damageFixed" variant="light-success" pill class="dmg-badge">
              <feather-icon icon="CheckIcon" size="10" class="mr-25"/>{{ msg('Fixed') }}
            </b-badge>
            <b-badge v-else variant="light-danger" pill class="dmg-badge">
              <feather-icon icon="AlertCircleIcon" size="10" class="mr-25"/>{{ msg('Open') }}
            </b-badge>
            <b-badge v-if="d.paid" variant="light-success" pill class="dmg-badge">
              <feather-icon icon="DollarSignIcon" size="10" class="mr-25"/>
              {{ d.paidByClient ? $t('paidByClient') : $t('paid') }}
            </b-badge>
          </div>
        </header>

        <!-- description -->
        <p class="dmg-card__desc" v-if="d.damageDescription">{{ d.damageDescription }}</p>
        <p class="dmg-card__desc dmg-card__desc--empty" v-else>
          <feather-icon icon="FileTextIcon" size="13" class="mr-50"/>
          <em>{{ msg('No description provided') }}</em>
        </p>

        <!-- footer chips -->
        <footer class="dmg-card__foot">
          <span v-if="d.damageCost != null && d.damageCost > 0" class="dmg-chip dmg-chip--cost">
            <feather-icon icon="EuroIcon" size="11" class="mr-25"/>{{ formatCurrency(d.damageCost) }}
          </span>
          <span v-if="d.reportedBy" class="dmg-chip">
            <feather-icon icon="UserIcon" size="11" class="mr-25"/>{{ d.reportedBy }}
          </span>
          <span v-if="d.images && d.images.length" class="dmg-chip dmg-chip--photo">
            <feather-icon icon="ImageIcon" size="11" class="mr-25"/>{{ d.images.length }}
          </span>
          <span v-if="d.isClientFault" class="dmg-chip dmg-chip--fault">
            <feather-icon icon="UserXIcon" size="11" class="mr-25"/>{{ msg('Client fault') }}
          </span>
          <a
              :href="`/real_estate/contracts/rent/${d.contractId}`"
              target="_blank"
              class="dmg-chip dmg-chip--link ml-auto"
              @click.stop
          >
            <feather-icon icon="ExternalLinkIcon" size="11" class="mr-25"/>{{ msg('Contract') }}
          </a>
        </footer>
      </article>
    </div>

    <!-- ── Empty state ──────────────────────────────────────── -->
    <div v-else class="dmg-empty">
      <div class="dmg-empty__icon">
        <feather-icon icon="ShieldIcon" size="32"/>
      </div>
      <h4>{{ msg('No damages reported') }}</h4>
      <p>{{ msg('All clear — no damage reports for this filter.') }}</p>
    </div>

    <!-- ── Pagination ───────────────────────────────────────── -->
    <div v-if="damages.length && pagination.total > pagination.amount" class="dmg-pagination">
      <b-pagination
          :disabled="loading"
          limit="9"
          :per-page="pagination.amount"
          :current-page="pagination.page"
          :total-rows="pagination.total"
          align="center"
          @input="onPaginationChange"
      />
    </div>

    <damage-modal ref="damageModal" @updated="refresh"/>
  </div>
</template>

<script>
import {mapActions} from "vuex";
import DamageModal from "@/components/modals/damage/damageModal.vue";

const getFilter = (buildingIds, realEstatesIds) => ({
  buildingIds: buildingIds,
  realEstatesIds: realEstatesIds,
  fixed: false,
  paid: false,
})

export default {
  name: "DamagePanel",
  components: {DamageModal},
  data: () => ({
    loading: true,
    filter: getFilter(),
    sort: {by: '', desc: false, direction: 'asc'},
    pagination: {amount: 12, page: 1, total: 0},
    damages: [],
  }),
  props: {
    buildingId: {
      type: String,
      required: false
    },
    realEstatesIds: {
      type: String,
      required: false
    }
  },
  watch: {
    buildingId: {
      handler() {
        this.refreshFilter()
      }
    },
    realEstatesIds: {
      handler() {
        this.refreshFilter()
      }
    }
  },
  created() {
    this.refreshFilter()
  },
  computed: {
    fixedCount() {
      return this.damages.filter(d => d.damageFixed).length;
    },
    openCount() {
      return this.damages.filter(d => !d.damageFixed).length;
    },
  },
  methods: {
    ...mapActions('damage', ['getAllDamage']),
    refreshFilter() {
      let buildingId = this.buildingId;
      let realEstatesIds = this.realEstatesIds;
      if (buildingId != null && !Array.isArray(buildingId)) {
        buildingId = [buildingId]
      }
      if (realEstatesIds != null && !Array.isArray(realEstatesIds)) {
        realEstatesIds = [realEstatesIds]
      }
      this.filter = {...this.filter, ...getFilter(buildingId, realEstatesIds)}
      this.pagination.page = 1
      this.refresh()
    },
    toggleFixed() {
      this.filter.fixed = !this.filter.fixed;
      this.pagination.page = 1;
      this.refresh();
    },
    togglePaid() {
      this.filter.paid = !this.filter.paid;
      this.pagination.page = 1;
      this.refresh();
    },
    refresh() {
      let $this = this;
      if (this.buildingId == null && this.realEstatesIds == null) {
        this.damages = [];
        return;
      }
      this.loading = true;
      this.getAllDamage({
        ...$this.pagination, filter: $this.filter, sortcolumn: $this.sort.by,
        sortby: $this.sort.direction,
      }).then(ans => {
        $this.damages = ans["Damages"] || [];
        if (ans["Total"] != null) $this.pagination.total = ans["Total"];
        else if (ans["TotalRows"] != null) $this.pagination.total = ans["TotalRows"];
      }, e => {
      }).finally(() => {
        $this.loading = false;
      });
    },
    onPaginationChange(page) {
      this.pagination.page = page;
      this.refresh();
    },
    onItemSelected(damage) {
      this.$refs.damageModal.showById(damage.id);
    },
    initials(name) {
      if (!name) return '?';
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },
    formatCurrency(value) {
      if (value == null) return '';
      try {
        return new Intl.NumberFormat('de-AT', {style: 'currency', currency: 'EUR'}).format(value);
      } catch (e) {
        return value + ' €';
      }
    },
  },
}
</script>

<style scoped lang="scss">
$dmg-radius: 14px;
$dmg-radius-lg: 16px;
$dmg-border: rgba(60, 60, 70, 0.08);
$dmg-border-dark: rgba(255, 255, 255, 0.06);
$dmg-primary: #7367f0;
$dmg-success: #28c76f;
$dmg-danger: #ea5455;
$dmg-warning: #ff9f43;

.dmg-panel { padding: 8px 4px; }

/* ── Toolbar ────────────────────────────────────────────── */
.dmg-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  padding: 4px 4px 16px 4px;
  border-bottom: 1px solid $dmg-border;
  margin-bottom: 16px;
}

.dmg-stats {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.dmg-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 8px;
  background: rgba(115, 103, 240, 0.04);
  border: 1px solid $dmg-border;
  border-radius: $dmg-radius;
  min-width: 130px;
}

.dmg-stat__icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  flex-shrink: 0;

  &--total {
    background: linear-gradient(135deg, $dmg-primary 0%, #5e50ee 100%);
    color: white;
    box-shadow: 0 4px 10px rgba(115, 103, 240, 0.3);
  }

  &--open {
    background: linear-gradient(135deg, $dmg-danger 0%, #d04344 100%);
    color: white;
    box-shadow: 0 4px 10px rgba(234, 84, 85, 0.3);
  }

  &--fixed {
    background: linear-gradient(135deg, $dmg-success 0%, #1f9d56 100%);
    color: white;
    box-shadow: 0 4px 10px rgba(40, 199, 111, 0.3);
  }
}

.dmg-stat__value {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.dmg-stat__label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary);
}

.dmg-filters {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dmg-filter {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid $dmg-border;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--secondary);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(115, 103, 240, 0.25);
    color: $dmg-primary;
  }

  &.active {
    background: $dmg-primary;
    border-color: $dmg-primary;
    color: white;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  }
}

/* ── Grid ───────────────────────────────────────────────── */
.dmg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 14px;
}

.dmg-card {
  position: relative;
  background: var(--card-bg, white);
  border: 1px solid $dmg-border;
  border-radius: $dmg-radius-lg;
  padding: 16px 18px 14px 18px;
  cursor: pointer;
  transition: all 0.18s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(34, 41, 47, 0.10);
    border-color: rgba(115, 103, 240, 0.25);
  }

  &--fixed {
    background: rgba(40, 199, 111, 0.03);
  }
}

.dmg-card__stripe {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;

  &--open {
    background: linear-gradient(90deg, $dmg-danger, transparent);
  }
  &--fixed {
    background: linear-gradient(90deg, $dmg-success, transparent);
  }
}

.dmg-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.dmg-card__client {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.dmg-card__avatar {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: linear-gradient(135deg, $dmg-primary 0%, #5e50ee 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 0.85rem;
  letter-spacing: 0.04em;
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  flex-shrink: 0;
}

.dmg-card__client-info {
  min-width: 0;
}

.dmg-card__name {
  font-size: 0.92rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 200px;
}

.dmg-card__time {
  font-size: 0.72rem;
  color: var(--secondary);
  margin-top: 2px;
  display: flex;
  align-items: center;
}

.dmg-card__badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.dmg-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 8px;
}

.dmg-card__desc {
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text);
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  &--empty {
    color: var(--secondary);
    font-size: 0.78rem;
    display: flex;
    align-items: center;
  }
}

.dmg-card__foot {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid $dmg-border;
}

.dmg-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.04);
  color: var(--secondary);

  &--cost {
    background: rgba(255, 159, 67, 0.12);
    color: $dmg-warning;
  }

  &--photo {
    background: rgba(115, 103, 240, 0.12);
    color: $dmg-primary;
  }

  &--fault {
    background: rgba(234, 84, 85, 0.12);
    color: $dmg-danger;
  }

  &--link {
    background: rgba(115, 103, 240, 0.08);
    color: $dmg-primary;
    text-decoration: none;
    transition: all 0.15s ease;

    &:hover {
      background: $dmg-primary;
      color: white;
    }
  }
}

/* ── Empty state ────────────────────────────────────────── */
.dmg-empty {
  text-align: center;
  padding: 48px 20px;
  border: 1.5px dashed $dmg-border;
  border-radius: $dmg-radius-lg;

  h4 {
    font-size: 1rem;
    font-weight: 700;
    margin: 12px 0 4px 0;
  }

  p {
    color: var(--secondary);
    font-size: 0.85rem;
    margin: 0;
  }
}

.dmg-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(40, 199, 111, 0.1);
  color: $dmg-success;
}

.dmg-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: var(--secondary);
  font-weight: 600;
}

.dmg-pagination {
  margin-top: 20px;
}

/* ── Dark mode ──────────────────────────────────────────── */
.dark-layout {
  .dmg-toolbar { border-bottom-color: $dmg-border-dark; }
  .dmg-stat,
  .dmg-card,
  .dmg-empty,
  .dmg-filter { border-color: $dmg-border-dark; }
  .dmg-card,
  .dmg-stat { background: #283046; }
  .dmg-stat { background: rgba(115, 103, 240, 0.08); }
  .dmg-card--fixed { background: rgba(40, 199, 111, 0.10); }
  .dmg-card__foot { border-top-color: $dmg-border-dark; }
  .dmg-card:hover { box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5); }

  // Filters
  .dmg-filter {
    background: rgba(255, 255, 255, 0.02);
    color: rgba(255, 255, 255, 0.65);

    &:hover { color: $dmg-primary; }
  }

  // Stat labels
  .dmg-stat__lbl { color: rgba(255, 255, 255, 0.55); }

  // Card content text
  .dmg-card__name { color: rgba(255, 255, 255, 0.95); }
  .dmg-card__time,
  .dmg-card__desc--empty { color: rgba(255, 255, 255, 0.5); }
  .dmg-card__desc { color: rgba(255, 255, 255, 0.85); }

  // Chips on dark
  .dmg-chip { background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.7); }
  .dmg-chip--cost { background: rgba(255, 159, 67, 0.18); color: #ffb872; }
  .dmg-chip--photo { background: rgba(115, 103, 240, 0.2); color: #b8b1ff; }
  .dmg-chip--fault { background: rgba(234, 84, 85, 0.2); color: #ff8a8b; }
  .dmg-chip--link {
    background: rgba(115, 103, 240, 0.16);
    color: #b8b1ff;

    &:hover { background: $dmg-primary; color: white; }
  }

  // Empty state
  .dmg-empty {
    color: rgba(255, 255, 255, 0.5);
    h4 { color: rgba(255, 255, 255, 0.9); }
  }

  .dmg-loading { color: rgba(255, 255, 255, 0.5); }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 600px) {
  .dmg-toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .dmg-grid {
    grid-template-columns: 1fr;
  }
  .dmg-card__name {
    max-width: 140px;
  }
}
</style>
